import { AlertCircle, AlertTriangle, Bell, CheckCircle, CreditCard, ExternalLink, Info, MessageSquare, Settings, ShoppingCart, UserPlus } from 'lucide-react'

import { NotificationCategory, NotificationType } from '../api/create-notification'
import { Notification } from '../hooks/use-notifications'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ago } from '@/utils/time'

// Translation function for categories
const getCategoryLabel = (category: NotificationCategory): string => {
  switch (category) {
    case 'order':
      return 'Commande'
    case 'system':
      return 'Système'
    case 'invitation':
      return 'Invitation'
    case 'message':
      return 'Message'
    case 'payment':
      return 'Paiement'
    case 'general':
    default:
      return 'Général'
  }
}

interface NotificationItemProps {
  notification: Notification
  onMarkAsRead: (id: string) => void
  onActionClick: (action: { category: NotificationCategory; url: string }) => void
}

export function NotificationItem({ notification, onMarkAsRead, onActionClick }: NotificationItemProps) {
  const TypeIcon = getTypeIcon(notification.type)
  const CategoryIcon = getCategoryIcon(notification.category)
  const typeColor = getTypeColor(notification.type)
  const categoryColor = getCategoryColor(notification.category)

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id)
    }
  }

  return (
    <div className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${!notification.is_read ? 'bg-blue-50/30' : ''}`} onClick={handleClick}>
      <div className="flex items-start gap-3">
        {/* Type Icon */}
        <div className={`flex-shrink-0 ${typeColor}`}>
          <TypeIcon className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {/* Category Badge */}
            <Badge variant="secondary" className={`text-xs ${categoryColor} border-0`}>
              <CategoryIcon className="w-3 h-3 mr-1" />
              {getCategoryLabel(notification.category)}
            </Badge>

            {/* Unread Indicator */}
            {!notification.is_read && <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />}
          </div>

          {/* Title */}
          {notification.title && <h4 className={`text-sm mb-1 ${!notification.is_read ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}>{notification.title}</h4>}

          {/* Message */}
          <p className="text-sm text-gray-600 leading-relaxed mb-2">{notification.message}</p>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">{ago(notification.created_at || 0)}</span>

            {/* Action Button */}
            {notification.action_url && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={(e) => {
                  e.stopPropagation()
                  onActionClick({ category: notification.category, url: notification.action_url! })
                }}
              >
                Voir
                <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export const getTypeIcon = (type: NotificationType) => {
  switch (type) {
    case 'success':
      return CheckCircle
    case 'error':
      return AlertCircle
    case 'warning':
      return AlertTriangle
    case 'info':
    default:
      return Info
  }
}

export const getTypeColor = (type: NotificationType) => {
  switch (type) {
    case 'success':
      return 'text-green-600'
    case 'error':
      return 'text-red-600'
    case 'warning':
      return 'text-amber-600'
    case 'info':
    default:
      return 'text-blue-600'
  }
}

export const getCategoryIcon = (category: NotificationCategory) => {
  switch (category) {
    case 'order':
      return ShoppingCart
    case 'system':
      return Settings
    case 'invitation':
      return UserPlus
    case 'message':
      return MessageSquare
    case 'payment':
      return CreditCard
    case 'general':
    default:
      return Bell
  }
}

export const getCategoryColor = (category: NotificationCategory) => {
  switch (category) {
    case 'order':
      return 'bg-purple-100 text-purple-800'
    case 'system':
      return 'bg-gray-100 text-gray-800'
    case 'invitation':
      return 'bg-blue-100 text-blue-800'
    case 'message':
      return 'bg-green-100 text-green-800'
    case 'payment':
      return 'bg-orange-100 text-orange-800'
    case 'general':
    default:
      return 'bg-slate-100 text-slate-800'
  }
}
